import '../assets/main.scss';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { gql, ApolloProvider } from '@apollo/client';
import client from '../components/lib/apolloClient';

import Lenis from 'lenis';

import DetectTouch from '../components/utils/detectTouch';
import SiteResize from '../components/utils/siteResize';
import {
  ThemeContextProvider,
  DesktopContextProvider,
  DimensionsContextProvider,
} from '../components/context/ThemeContextManagement';
import { VideoLightboxProvider } from '../components/context/VideoLightboxContext';
import { getCompiledGlobalQuery } from '../components/utils/compileGlobalQuery.wp';
import { transformGlobalData } from '../components/utils/compileGlobalTransforms.wp';
import SiteLayout from '../components/layout/SiteLayout';
import ErrorBoundary from '../components/fragments/error-boundary/ErrorBoundary';
import { fontSeasonSans, fontSeasonMix } from '../components/lib/fonts';
import {
  registerRouteMedia,
  prefetchRouteMedia,
  warmVideoRuntimeWhenIdle,
} from '../components/utils/mediaPrefetch';

import { AnimatePresence } from 'motion/react';
import PageTransition from '../components/fragments/page-transition/PageTransition';

import Script from 'next/script';

import { GoogleTagManager } from '@next/third-parties/google';

function App({ Component, pageProps, globalData }) {
  const router = useRouter();
  const isInitialLoad = useRef(true);
  const hasNavigated = useRef(false); // Track if app has mounted

  // `router.asPath` is NOT the resolved URL while Vercel server-renders a
  // statically generated page — it resolved to something other than "/" there,
  // so anything keyed off it that affects markup (`isHome`, and the dynamic
  // colour vars derived from it) rendered differently on the client than in the
  // server HTML, and hydration bailed out with React #418/#423 on every hard
  // refresh of the homepage. getStaticProps passes the resolved path down
  // instead, which serialises into __NEXT_DATA__ and is therefore identical on
  // both sides. Pages that don't supply it fall back to asPath.
  const routePath = pageProps?.routePath ?? router.asPath.split('?')[0];

  useEffect(() => {
    // `lang` is set server-side in _document.js so it's in the initial HTML.
    // next/font can't be used in _document.js, so the font variable classes
    // are applied here instead — html is the common ancestor for both the
    // #__next tree and anything portaled straight to document.body.
    document.documentElement.classList.add(
      fontSeasonSans.variable,
      fontSeasonMix.variable
    );
  }, []);

  // The homepage hero starts playing an HLS stream the moment it mounts, and a
  // client-side navigation gives it no head start — no loader, no preconnect,
  // and nothing of the stream in cache. Tell the prefetcher which URL sits
  // behind "/" so SmartLink can warm it on hover, and pull the hls.js chunk in
  // while the browser is idle. Both no-op on a metered/slow connection.
  useEffect(() => {
    registerRouteMedia('/', globalData?.homeHeroVideoUrl);
    return warmVideoRuntimeWhenIdle();
  }, [globalData?.homeHeroVideoUrl]);

  useEffect(() => {
    const handleStart = (url) => {
      hasNavigated.current = true;
      // Backstop for navigations that never produce a hover: keyboard
      // activation, back/forward, router.push. Later than SmartLink's hover
      // hook, but still overlapped with the destination's own chunk loading.
      prefetchRouteMedia(url);
    };

    const handleComplete = () => {
      // Meta Pixel: fire a PageView on client-side navigations (the inline
      // init script only fires it on the initial load).
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'PageView');
      }

      // The header is mounted once in SiteLayout and survives client-side
      // navigation, so a clicked nav link keeps DOM focus on the new page —
      // which leaves the nav's focus-based "don't hide while I'm being used"
      // rules matching forever. Hand focus back to the document, but only if
      // it's still inside the header, so page content never loses it.
      const active = document.activeElement;
      if (active && active !== document.body && active.closest?.('#header')) {
        active.blur();
      }

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 1000);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
    };
  }, [router.asPath.split('?')[0]]);

  // Initialize Lenis
  useEffect(() => {
    const lenis = new Lenis({ stopInertiaOnNavigate: true });
    window.lenis = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Handle hash/anchor on page load
    const hash = window.location.hash;
    let anchorTimeout;

    if (hash && hash.startsWith('#')) {
      // Wait for DOM to be ready and Lenis to initialize
      anchorTimeout = setTimeout(() => {
        const target = document.querySelector(
          `[data-anchor="${hash.slice(1)}"]`
        );
        if (target) {
          const yOffset = -100;
          const targetY =
            target.getBoundingClientRect().top + window.scrollY + yOffset;
          lenis.scrollTo(targetY, { immediate: true });
        }
      }, 100);
    }

    // Without this the raf loop runs forever and Lenis' own listeners are never
    // removed, so instances stack up on every hot reload (and on every strict
    // mode double-mount in development).
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(anchorTimeout);
      lenis.destroy();
      if (window.lenis === lenis) delete window.lenis;
    };
  }, []);

  // True for a hard load, false for every client-side navigation. Passed down
  // to the page so HeroHome can decide whether to play the homepage's
  // first-load loader — the only thing on the site that cares.
  const isFirstPage = isInitialLoad.current && !hasNavigated.current;
  const isHome = routePath === '/';

  // useEffect(() => {
  //   // Native browser scrolling only (Lenis disabled).
  //   window.lenis = null;
  //   if (isFirstPage) {
  //     if ('scrollRestoration' in window.history) {
  //       window.history.scrollRestoration = 'manual';
  //     }
  //     window.scrollTo(0, 0);
  //   }
  // }, []);

  return (
    <>
      {/* {process.env.NEXT_PUBLIC_BUGHERD_ID && (
        <Script
          strategy="afterInteractive"
          src={`https://www.bugherd.com/sidebarv2.js?apikey=${process.env.NEXT_PUBLIC_BUGHERD_ID}`}
        />
      )} */}

      {/* Meta Pixel Code */}
      {/* lazyOnload rather than afterInteractive: fbevents.js is ~185KB and
          ~146ms of main-thread time, and it was landing inside the Total
          Blocking Time window on every page. Waiting for window.load moves it
          out of that window entirely. The trade-off is that visitors who leave
          before load fires stop being counted — accepted deliberately. */}
      <Script id="meta-pixel" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '1140964389616155');
fbq('track', 'PageView');`}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=1140964389616155&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
      {/* End Meta Pixel Code */}

      <GoogleTagManager gtmId="GTM-P95G32RL" />

      <ApolloProvider client={client}>
        <DesktopContextProvider>
          <DimensionsContextProvider>
            <ThemeContextProvider>
              <VideoLightboxProvider>
                <SiteResize />
                <DetectTouch />
                <SiteLayout
                  globalData={globalData}
                  isDraftModeEnabled={pageProps?.isDraftModeEnabled || false}
                >
                  {/* resetKey clears the error state on navigation, otherwise
                      one throwing block poisons every subsequent route. */}
                  <ErrorBoundary resetKey={routePath}>
                    <AnimatePresence
                      mode="popLayout"
                      // initial={false}
                    >
                      <PageTransition
                        key={router.asPath.split('?')[0]}
                        path={routePath}
                        pageData={pageProps?.data?.pageData}
                        postType={pageProps?.data?.postType}
                        isInitialLoad={isFirstPage}
                        isHome={isHome}
                      >
                        <Component
                          globalData={globalData}
                          {...pageProps}
                          isInitialLoad={isFirstPage}
                          isHome={isHome}
                        />
                      </PageTransition>
                    </AnimatePresence>
                  </ErrorBoundary>
                </SiteLayout>
              </VideoLightboxProvider>
            </ThemeContextProvider>
          </DimensionsContextProvider>
        </DesktopContextProvider>
      </ApolloProvider>
    </>
  );
}

// Nav/footer data barely changes, but App.getInitialProps re-runs on every SSR
// request AND every client-side route change. Cache it for a window matching
// the page ISR revalidate interval instead of refetching on every navigation.
const GLOBAL_DATA_TTL_MS = 60_000;
let globalDataCache = null; // { data, expiresAt }
let globalDataPromise = null; // in-flight fetch, deduped across concurrent callers

async function fetchGlobalData() {
  if (globalDataCache && globalDataCache.expiresAt > Date.now()) {
    return globalDataCache.data;
  }

  if (!globalDataPromise) {
    globalDataPromise = (async () => {
      const compiledGlobalQuery = getCompiledGlobalQuery();
      const { data } = await client.query({
        query: gql`
          ${compiledGlobalQuery}
        `,
        fetchPolicy: 'no-cache',
      });
      const transformed = transformGlobalData(data);
      globalDataCache = {
        data: transformed,
        expiresAt: Date.now() + GLOBAL_DATA_TTL_MS,
      };
      return transformed;
    })()
      .catch((error) => {
        // Serve stale nav/footer data rather than crashing the whole app on a
        // transient WordPress outage.
        if (globalDataCache) return globalDataCache.data;
        throw error;
      })
      .finally(() => {
        globalDataPromise = null;
      });
  }

  return globalDataPromise;
}

// Routes that end the response inside their own getServerSideProps and never
// render any UI. Next resolves App.getInitialProps *before* the page's
// getServerSideProps (render.js calls loadGetInitialProps(App) first, then
// bails out of rendering once it sees the response was already sent), so
// without this list /robots.txt and /sitemap.xml each block on a WordPress
// round-trip for nav and footer data that is then thrown away. On a cold
// function or a slow CMS that was enough to make Google's robots.txt fetch
// time out.
const CHROMELESS_ROUTES = new Set(['/robots.txt', '/sitemap.xml']);

App.getInitialProps = async (appContext) => {
  const isChromeless = CHROMELESS_ROUTES.has(appContext?.ctx?.pathname);
  const props = {
    globalData: isChromeless ? null : await fetchGlobalData(),
  };

  // A custom App.getInitialProps suppresses the page's own getInitialProps
  // unless it's resolved here. Only pages/_error.js defines one (to read the
  // status code) — every other page uses getStaticProps/getServerSideProps,
  // whose props Next supplies directly, so `pageProps` is only set when the
  // page actually has a getInitialProps to run and can't clobber them.
  if (appContext?.Component?.getInitialProps) {
    props.pageProps = await appContext.Component.getInitialProps(
      appContext.ctx
    );
  }

  return props;
};

export default App;
